import React from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import Dialog from 'shared/modules/common/components/atoms/Dialog';
import BadgeAppStore from 'web/assets/images/badge-apple-app-store.png';
import BadgePlayStore from 'web/assets/images/badge-google-play-store.png';
import QRCodeAppStore from 'web/assets/images/download-link-qr-code-app-store.png';
import QRCodePlayStore from 'web/assets/images/download-link-qr-code-play-store.png';
import QRCodeAppleOnboardingPROD from 'web/assets/images/qr-code-apple-onboarding-prod.png';
import QRCodeAppleOnboardingSTG from 'web/assets/images/qr-code-apple-onboarding-stg.png';
import AppStoreBadgeLinks from 'web/modules/common/components/molecules/AppStoreBadgeLinks';

type Props = {
  open?: boolean;
  onCancel?: () => void;
  variant?: string;
};

const AppDownload = (props: Props) => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const isPROD =
    process.env.WEB_ENV && process.env.WEB_ENV === 'production' ? true : false;
  const variant = props.variant || 'default';

  // Rather creating than creating a new app downlaod dialog that redirect to specific screen in the app, we are using the existing one and passing a variant to it.
  const getQRCodeDisplayByVariant = (targetVariant: string | undefined) => {
    if (targetVariant === 'apple-onboarding') {
      if (isPROD) {
        return {
          ios: QRCodeAppleOnboardingPROD,
          android: QRCodeAppleOnboardingPROD,
        };
      } else {
        return {
          ios: QRCodeAppleOnboardingSTG,
          android: QRCodeAppleOnboardingSTG,
        };
      }
    } else {
      return {
        ios: QRCodeAppStore,
        android: QRCodePlayStore,
      };
    }
  };

  const getBadgeLinksByVariant = (targetVariant: string | undefined) => {
    if (targetVariant === 'apple-onboarding') {
      return isPROD
        ? {
            ios: 'https://d9r4z.app.goo.gl/6k72',
            android: 'https://d9r4z.app.goo.gl/6k72',
          }
        : isBigScreen
        ? {
            ios: 'https://apps.apple.com/jp/app/paidy/id1220373112',
            android:
              'https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja',
          }
        : {
            ios: 'https://paidy2.page.link/VAMe',
            android: 'https://paidy2.page.link/VAMe',
          };
    } else {
      return {
        ios: 'https://apps.apple.com/jp/app/paidy/id1220373112',
        android:
          'https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja',
      };
    }
  };

  return (
    <Dialog
      title=""
      open={props.open}
      onCloseButtonPress={() => props.onCancel?.()}
      onBackdropPress={() => props.onCancel?.()}
      id="app-download-link-dialog"
      style={style.dialog}
    >
      <Text style={style.title}>
        {`毎月のご請求のお支払いも、\nコンビニでアプリを見せるだけ。`}
      </Text>
      <Text style={style.text}>
        アプリからペイディプラスに無料アップグレードすると、３・６・１２回あと払いやどこでもペイディなど便利な機能をご利用いただけます。アプリのダウンロードはこちら。
      </Text>
      <View style={style.qrCodeWrapper}>
        {isBigScreen ? (
          <View style={style.row}>
            <View style={style.col}>
              <TouchableOpacity
                accessibilityRole="link"
                href={getBadgeLinksByVariant(variant).ios}
                hrefAttrs={{
                  target: '_blank',
                }}
              >
                <Image source={{ uri: BadgeAppStore }} style={style.badge} />
              </TouchableOpacity>
              <Image
                source={{ uri: getQRCodeDisplayByVariant(variant).ios }}
                style={style.qrCode}
              />
            </View>
            <View style={style.col}>
              <TouchableOpacity
                accessibilityRole="link"
                href={getBadgeLinksByVariant(variant).android}
                hrefAttrs={{
                  target: '_blank',
                }}
              >
                <Image source={{ uri: BadgePlayStore }} style={style.badge} />
              </TouchableOpacity>
              <Image
                source={{ uri: getQRCodeDisplayByVariant(variant).android }}
                style={style.qrCode}
              />
            </View>
          </View>
        ) : (
          <AppStoreBadgeLinks
            iosLink={getBadgeLinksByVariant(variant).ios}
            androidLink={getBadgeLinksByVariant(variant).android}
          />
        )}
      </View>
    </Dialog>
  );
};

const style = StyleSheet.create({
  title: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 24,
  },
  text: {
    fontSize: 16,
    fontWeight: '300',
    marginBottom: 24,
  },
  qrCode: {
    width: 180,
    height: 180,
    marginTop: 16,
  },
  qrCodeWrapper: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 24,
  },
  row: {
    flexDirection: 'row',
  },
  col: {
    paddingHorizontal: 8,
  },
  badge: {
    width: '100%',
    aspectRatio: 3.4,
    resizeMode: 'contain',
  },
  dialog: {
    maxHeight: 624,
  },
});

export default AppDownload;
